// Pyramid (owner 2026-10-24: the next game after FreeCell, the one every Polish source names). One deck: 28 cards face
// up in a pyramid of 7 rows (1 at the top .. 7 at the bottom), each card half covered by the two below it; the rest is
// the stock, its top card face up and in play. A card is free when both cards below it are gone (the bottom row always
// is). Two free cards that make 13 (A=1, J=11, Q=12) leave together for the discard pile (the one foundation); a King
// leaves alone. A click on the stock puts its top card on the waste, whose top card is in play too. The empty stock
// takes the waste back twice: three passes in all, as in Microsoft Solitaire Collection. The game is won when the
// pyramid is cleared (cards may stay in the stock and the waste). Owner's choices: .ai/game-rules.md "Pyramid".
//
// Each place of the pyramid is a tableau pile of 0 or 1 card: row r, place p (0..r) is pile r(r+1)/2 + p.
import type { CardKey, GameState, PileRef } from '../types';
import { rankLow } from '../cards';
import { card, pileCards, topOf, type VariantRules } from './base';

export const PYRAMID_ROWS = 7;

/** Row and place of pyramid pile `i`. */
export function pyramidPlace(i: number): { row: number; place: number } {
  const row = Math.floor((Math.sqrt(8 * i + 1) - 1) / 2);
  return { row, place: i - (row * (row + 1)) / 2 };
}

export const pyramidIndex = (row: number, place: number): number => (row * (row + 1)) / 2 + place;

/** No card lies on pyramid pile `i` (both piles below it are empty, or it is in the bottom row). */
export function isUncovered(s: GameState, i: number): boolean {
  const { row, place } = pyramidPlace(i);
  if (row >= PYRAMID_ROWS - 1) return true;
  const t = s.piles.tableau;
  return !t[pyramidIndex(row + 1, place)]?.length && !t[pyramidIndex(row + 1, place + 1)]?.length;
}

/** The pile's top card is in play: a free pyramid card, the waste's top, the stock's (face-up) top. */
function inPlay(s: GameState, pile: PileRef): CardKey | undefined {
  if (pile.kind === 'tableau') return isUncovered(s, pile.index) ? s.piles.tableau[pile.index]?.[0] : undefined;
  if (pile.kind === 'waste' || pile.kind === 'stock') return topOf(pileCards(s, pile));
  return undefined;
}

const value = (s: GameState, key: CardKey): number => rankLow(card(s, key).rank);

function canLift(s: GameState, pile: PileRef, index: number): boolean {
  const cards = pileCards(s, pile);
  return index === cards.length - 1 && inPlay(s, pile) !== undefined;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const key = run[0];
  if (key === undefined || run.length !== 1) return false;
  if (target.kind === 'foundation') return target.index === 0 && value(s, key) === 13;
  const other = inPlay(s, target);
  return other !== undefined && other !== key && value(s, key) + value(s, other) === 13;
}

export const pyramid: VariantRules = {
  id: 'pyramid',
  columns: Array.from({ length: (PYRAMID_ROWS * (PYRAMID_ROWS + 1)) / 2 }, () => 1),
  foundations: 1,
  cells: 0,
  decks: 1,
  suits: ['c', 's', 'd', 'h'],
  variableSuits: 0,
  turn: 1,
  hasWaste: true,
  recycle: true,
  maxRecycles: 2,
  stockTopUp: true,
  pairs: true,
  allFaceUp: true,
  autoMove: false,
  foundationToTableau: false,
  canLift,
  canDrop,
  foundationFor: (s, key) => (value(s, key) === 13 ? 0 : null),
  buildsOn: () => false,
  completedRun: () => false,
  isWon: (s) => s.piles.tableau.every((p) => p.length === 0),
};
